import GL from "_src/engine/gl";
import extensions from "_src/engine/extensions";
import {
  blendFactor,
  blendEquation,
  stencilAction,
  stencilFunc,
  triangleSide,
  textureWrapper,
  textureMinFilter,
  textureMagFilter,
  RBIntFormat,
  cullingFrontFace,
  CompressedTextureFormat, cubeMapTarget
} from "_src/engine/constants";

export default {

  getBlendFactor(factor) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(factor) {
      case blendFactor.ZERO:
        return gl.ZERO;
      case blendFactor.ONE:
        return gl.ONE;
      case blendFactor.SRC_ALPHA:
        return gl.SRC_ALPHA;
      case blendFactor.ONE_MINUS_SRC_ALPHA:
        return gl.ONE_MINUS_SRC_ALPHA;
      case blendFactor.DST_ALPHA:
        return gl.DST_ALPHA;
      case blendFactor.ONE_MINUS_DST_ALPHA:
        return gl.ONE_MINUS_DST_ALPHA;
      case blendFactor.CONSTANT_ALPHA:
        return gl.CONSTANT_ALPHA;
      case blendFactor.ONE_MINUS_CONSTANT_ALPHA:
        return gl.ONE_MINUS_CONSTANT_ALPHA;
      case blendFactor.SRC_COLOR:
        return gl.SRC_COLOR;
      case blendFactor.ONE_MINUS_SRC_COLOR:
        return gl.ONE_MINUS_SRC_COLOR;
      case blendFactor.DST_COLOR:
        return gl.DST_COLOR;
      case blendFactor.ONE_MINUS_DST_COLOR:
        return gl.ONE_MINUS_DST_COLOR;
      default:
        return gl.ZERO;

    }

  },

  getBlendEquation(func) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    let ext = null;

    switch(func) {
      case blendEquation.FUNC_ADD:
        return gl.FUNC_ADD;
      case blendEquation.FUNC_SUBTRACT:
        return gl.FUNC_SUBTRACT;
      case blendEquation.FUNC_REVERSE_SUBTRACT:
        return gl.FUNC_REVERSE_SUBTRACT;
      case blendEquation.MIN:
        ext = extensions.get("EXT_blend_minmax");
        return GL.checkWebGL2() ? gl.MIN : (ext ? ext.MIN : gl.FUNC_ADD);
      case blendEquation.MAX:
        ext = extensions.get("EXT_blend_minmax");
        return GL.checkWebGL2() ? gl.MAX : (ext ? ext.MAX : gl.FUNC_ADD);
      default:
        return gl.FUNC_ADD;

    }

  },

  getStencilAction(action) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(action) {
      case stencilAction.KEEP:
        return gl.KEEP;
      case stencilAction.ZERO:
        return gl.ZERO;
      case stencilAction.REPLACE:
        return gl.REPLACE;
      case stencilAction.INCR:
        return gl.INCR;
      case stencilAction.INCR_WRAP:
        return gl.INCR_WRAP;
      case stencilAction.DECR:
        return gl.DECR;
      case stencilAction.DECR_WRAP:
        return gl.DECR_WRAP;
      case stencilAction.INVERT:
        return gl.INVERT;
      default:
        return gl.KEEP;
    }

  },

  getStencilFunction(func) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(func) {
      case stencilFunc.NEVER:
        return gl.NEVER;
      case stencilFunc.LESS:
        return gl.LESS;
      case stencilFunc.EQUAL:
        return gl.EQUAL;
      case stencilFunc.LEQUAL:
        return gl.LEQUAL;
      case stencilFunc.GREATER:
        return gl.GREATER;
      case stencilFunc.NOTEQUAL:
        return gl.NOTEQUAL;
      case stencilFunc.GEQUAL:
        return gl.GEQUAL;
      case stencilFunc.ALWAYS:
        return gl.ALWAYS;
      default:
        return gl.ALWAYS;
    }

  },

  getTriangleSide(side) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(side) {
      case triangleSide.FRONT:
        return gl.FRONT;
      case triangleSide.BACK:
        return gl.BACK;
      case triangleSide.FRONT_AND_BACK:
        return gl.FRONT_AND_BACK;
      default:
        return gl.FRONT_AND_BACK;
    }

  },

  getWrapBehavior(wrapper) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(wrapper) {
      case textureWrapper.CLAMP:
        return gl.CLAMP_TO_EDGE;
      case textureWrapper.REPEAT:
        return gl.REPEAT;
      case textureWrapper.MIRROR:
        return gl.MIRRORED_REPEAT;
      default:
        return gl.CLAMP;
    }

  },

  getMagFilter(filter) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(filter) {
      case textureMagFilter.NEAREST:
        return gl.NEAREST;
      case textureMagFilter.LINEAR:
        return gl.LINEAR;
      default:
        return gl.NEAREST;
    }

  },

  getMinFilter(filter) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(filter) {
      case textureMinFilter.NEAREST:
        return gl.NEAREST;
      case textureMinFilter.LINEAR:
        return gl.LINEAR;
      case textureMinFilter.NEAREST_MIPMAP_NEAREST:
        return gl.NEAREST_MIPMAP_NEAREST;
      case textureMinFilter.LINEAR_MIPMAP_NEAREST:
        return gl.LINEAR_MIPMAP_NEAREST;
      case textureMinFilter.NEAREST_MIPMAP_LINEAR:
        return gl.NEAREST_MIPMAP_LINEAR;
      case textureMinFilter.LINEAR_MIPMAP_LINEAR:
        return gl.LINEAR_MIPMAP_LINEAR;
      default:
        return gl.NEAREST;
    }

  },

  getRBInternalFormat(format) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(format) {
      case RBIntFormat.R4G4B4A4:
        return gl.RGBA4;
      case RBIntFormat.R5G6B5:
        return gl.RGB565;
      case RBIntFormat.R5G5B5A1:
        return gl.RGB5_A1;
      case RBIntFormat.R8G8B8A8:
        return gl.RGBA8;
      case RBIntFormat.D16U:
        return gl.DEPTH_COMPONENT16;
      case RBIntFormat.D24U:
        return gl.DEPTH_COMPONENT24;
      case RBIntFormat.S8:
        return gl.STENCIL_INDEX8;
      case RBIntFormat.DS:
        return GL.checkWebGL2() ? gl.DEPTH24_STENCIL8 : gl.DEPTH_STENCIL;
      case RBIntFormat.D24S8:
        return gl.DEPTH24_STENCIL8;
      default:
        return gl.RGB565;
    }

  },

  getRBElementSize(format, samples) {
    switch (format) {
      case RBIntFormat.R4G4B4A4:
      case RBIntFormat.R5G6B5:
      case RBIntFormat.R5G5B5A1:
        return 4;
      case RBIntFormat.DS:
      case RBIntFormat.D24S8:
        return 4 * samples;
      case RBIntFormat.R8G8B8A8:
        return 8;
      case RBIntFormat.D16U:
        return 2 * samples;
      case RBIntFormat.D24U:
        return 3 * samples;
      case RBIntFormat.S8:
        return 1 * samples;
    }
  },

  getFrontFace(front) {

    const gl = GL.getContext();

    if (!gl) {
      return null;
    }

    switch(front) {
      case cullingFrontFace.CLOCK_WISE:
        return gl.CW;
      default:
        return gl.CCW;
    }

  },

  getCompressedTextureInternalFormat(format) {
    const ext =
        extensions.get("WEBGL_compressed_texture_s3tc") ||
        extensions.get("MOZ_WEBGL_compressed_texture_s3tc") ||
        extensions.get("WEBKIT_WEBGL_compressed_texture_s3tc");

    switch(format) {
      case CompressedTextureFormat.DXT1:
        return ext.COMPRESSED_RGBA_S3TC_DXT1_EXT;
      case CompressedTextureFormat.DXT3:
        return ext.COMPRESSED_RGBA_S3TC_DXT3_EXT;
      case CompressedTextureFormat.DXT5:
        return ext.COMPRESSED_RGBA_S3TC_DXT5_EXT;
    }
  },

  getCubeMapTarget(target) {
    const gl = GL.getContext();
    if (!gl) return null;

    switch(target) {
      case cubeMapTarget.POSITIVE_X:
        return gl.TEXTURE_CUBE_MAP_POSITIVE_X;
      case cubeMapTarget.NEGATIVE_X:
        return gl.TEXTURE_CUBE_MAP_NEGATIVE_X;
      case cubeMapTarget.POSITIVE_Y:
        return gl.TEXTURE_CUBE_MAP_POSITIVE_Y;
      case cubeMapTarget.NEGATIVE_Y:
        return gl.TEXTURE_CUBE_MAP_NEGATIVE_Y;
      case cubeMapTarget.POSITIVE_Z:
        return gl.TEXTURE_CUBE_MAP_POSITIVE_Z;
      case cubeMapTarget.NEGATIVE_Z:
        return gl.TEXTURE_CUBE_MAP_NEGATIVE_Z;
    }
  }

}
